/**
 * Nosto-QuickBuyModal.js
 *
 * Administra el drawer global de quick buy utilizado por
 * los product cards de Nosto.
 *
 * El contenido interno utiliza el HTML y los custom elements
 * originales de Broadcast.
 *
 * Administra:
 * - apertura y cierre del drawer;
 * - carga AJAX del producto;
 * - cambio entre productos relacionados por color;
 * - preservación de la posición de los colores;
 * - preservación del scroll horizontal de colores;
 * - slider de imágenes;
 * - estados de carga;
 * - tracking.
 */

(function initializeNostoQuickBuyModal(global) {
    "use strict";

    class NostoQuickBuyModal {
        constructor({
            modal = "[data-nosto-quick-buy-modal]",
            viewService = null,
            onOpen = null,
            onClose = null,
            onProductChange = null,
            onError = null,
            debug = false,
        } = {}) {
            this.modal = this.resolveElement(modal);
            this.viewService = viewService;

            this.onOpen =
                typeof onOpen === "function"
                    ? onOpen
                    : null;

            this.onClose =
                typeof onClose === "function"
                    ? onClose
                    : null;

            this.onProductChange =
                typeof onProductChange === "function"
                    ? onProductChange
                    : null;

            this.onError =
                typeof onError === "function"
                    ? onError
                    : null;

            this.debug = Boolean(debug);

            this.content = null;
            this.loadingElement = null;
            this.loadErrorElement = null;

            this.root = null;

            this.activeProductId = "";
            this.activeHandle = "";
            this.pendingProductId = "";
            this.activeProductUrl = "";

            this.triggerButton = null;

            this.activeRequestController = null;
            this.requestSequence = 0;

            this.isInitialized = false;
            this.isOpen = false;
            this.isLoading = false;

            this.nostoClient = null;

            /*
             * Estado utilizado para preservar la posición
             * de los colores entre cambios AJAX.
             */
            this.colorOrder = [];
            this.activeColorIndex = 0;
            this.colorScrollLeft = 0;

            this.destroyImageSlider = null;
            this.destroyColorSlider = null;

            this.activeContext = {};
            this.activeHit = null;
            this.activeImageIndex = 0;

            this.boundHandleOpenRequest =
                this.handleOpenRequest.bind(this);

            this.boundHandleClick =
                this.handleClick.bind(this);

            this.boundHandleCancel =
                this.handleCancel.bind(this);

            this.boundHandleClose =
                this.handleClose.bind(this);

            this.boundHandleProductAdd =
                this.handleProductAdd.bind(this);

            this.boundHandleProductAddError =
                this.handleProductAddError.bind(this);
        }

        log(...args) {
            if (!this.debug) {
                return;
            }

            console.info(
                "[NostoQuickBuyModal]",
                ...args
            );
        }

        normalizeString(value) {
            return String(value ?? "").trim();
        }

        normalizeId(value) {
            const normalizedValue =
                this.normalizeString(value);

            if (!normalizedValue) {
                return "";
            }

            return normalizedValue
                .split("/")
                .filter(Boolean)
                .at(-1);
        }

        normalizeProductUrl(value) {
            const normalizedUrl =
                this.normalizeString(value);

            if (!normalizedUrl) {
                return "";
            }

            try {
                return new URL(
                    normalizedUrl,
                    global.location.origin
                ).href;
            } catch (error) {
                this.log(
                    "No fue posible normalizar la URL del producto.",
                    {
                        value,
                        error,
                    }
                );

                return "";
            }
        }
        isProductPage() {
            const pathname =
                global.location?.pathname || "";

            return /^\/products\/[^/]+/.test(
                pathname
            );
        }

        buildQuickBuyUrl(url) {
            const normalizedUrl =
                this.normalizeString(url);

            if (!normalizedUrl) {
                return "";
            }

            try {
                const quickBuyUrl =
                    new URL(
                        normalizedUrl,
                        global.location.origin
                    );

                /*
                 * Si el Quick Buy se abre desde una página
                 * de producto, enviamos explícitamente el contexto.
                 */
                if (this.isProductPage()) {
                    quickBuyUrl.searchParams.set(
                        "quick_buy_context",
                        "product"
                    );
                } else {
                    quickBuyUrl.searchParams.delete(
                        "quick_buy_context"
                    );
                }

                /*
                 * IMPORTANTE:
                 * Shopify necesita saber qué section debe devolver.
                 */
                quickBuyUrl.searchParams.set(
                    "section_id",
                    "nosto-product-quick-buy"
                );

                return quickBuyUrl.href;

            } catch (error) {
                this.log(
                    "No fue posible construir la URL del Quick Buy.",
                    {
                        url,
                        error,
                    }
                );

                return normalizedUrl;
            }
        }

        resolveElement(target) {
            if (
                typeof Element !== "undefined" &&
                target instanceof Element
            ) {
                return target;
            }

            if (typeof target !== "string") {
                return null;
            }

            return document.querySelector(target);
        }

        resolveViewService() {
            if (this.viewService) {
                return this.viewService;
            }

            this.viewService =
                global.NOSTO_PLP
                    ?.productViewService ||
                global.NOSTO_SEARCH
                    ?.productViewService ||
                global.NOSTO_PRODUCT_VIEW_SERVICE ||
                null;

            return this.viewService;
        }

        resolveNostoClient() {
            if (this.nostoClient) {
                return this.nostoClient;
            }

            this.nostoClient =
                global.NOSTO_PLP?.client ||
                global.NOSTO_FEATURED_PLP?.client ||
                global.NOSTO_CAROUSEL_PLP?.client ||
                global.NOSTO_SEARCH?.client ||
                null;

            if (
                !this.nostoClient &&
                typeof global.NostoClient === "function"
            ) {
                this.nostoClient =
                    new global.NostoClient({
                        debug: this.debug,
                    });
            }

            return this.nostoClient;
        }

        normalizeTrackingType(value) {
            const track =
                this.normalizeString(value).toLowerCase();

            if (track === "nosto-category") {
                return "category";
            }

            if (
                track === "nosto-search" ||
                track === "search"
            ) {
                return "serp";
            }

            if (
                track === "nosto-recommendation" ||
                track === "carousel-recommendation"
            ) {
                return "recommendation";
            }

            return track;
        }

        async trackSuccessfulAdd(detail = {}) {
            const context =
                detail.context || {};

            const source =
                this.normalizeString(
                    context.source || ""
                ).toLowerCase();

            const track =
                this.normalizeTrackingType(
                    context.track || source
                );

            const productId =
                this.normalizeId(
                    detail.productId
                );

            const attributionKey =
                this.normalizeString(
                    context.attributionKey ||
                    context.resultId ||
                    ""
                ).replace(
                    /^\?nosto=/,
                    ""
                );

            const client =
                this.resolveNostoClient();

            if (!client || !productId) {
                return false;
            }

            try {
                /*
                 * El track decide la familia de API. Search y
                 * Category van primero para que un attributionKey
                 * presente en su card no los mande a recommendations.
                 */
                const isSearchTrack =
                    track === "category" ||
                    track === "serp" ||
                    track === "autocomplete";

                if (!isSearchTrack) {
                    if (!attributionKey) {
                        return false;
                    }

                    return Boolean(
                        await client.recordAttribution({
                            productId,
                            attributionKey,
                            type: "cp",
                            refType: "api",
                        })
                    );
                }

                const hit = {
                    ...(detail.hit || {}),

                    productId:
                        this.normalizeId(
                            detail.hit?.productId ||
                            productId
                        ),

                    url:
                        this.normalizeProductUrl(
                            detail.hit?.url ||
                            detail.productUrl ||
                            this.activeProductUrl
                        ),
                };

                if (
                    !hit.productId ||
                    !hit.url
                ) {
                    return false;
                }

                const recorded =
                    await client.recordSearchAddToCart(
                        track,
                        hit
                    );

                return Boolean(
                    recorded
                );
            } catch (error) {
                console.error(
                    "[NostoQuickBuyModal] No fue posible registrar la atribución del Quick Buy.",
                    error
                );

                return false;
            }
        }

        unbindLegacyQuickBuyTracking() {
            const handler =
                global.NOSTO_QUICK_BUY_ADD_TRACKING_HANDLER;

            if (typeof handler === "function") {
                document.removeEventListener(
                    "nosto:quick-buy:add",
                    handler
                );
            }

            global.NOSTO_QUICK_BUY_ADD_TRACKING_BOUND =
                false;

            global.NOSTO_QUICK_BUY_ADD_TRACKING_HANDLER =
                null;
        }

        validateDependencies() {
            if (!this.modal) {
                throw new Error(
                    "No se encontró [data-nosto-quick-buy-modal]."
                );
            }

            const viewService =
                this.resolveViewService();

            if (
                !viewService ||
                typeof viewService.getQuickBuy !==
                "function"
            ) {
                throw new Error(
                    "NostoQuickBuyModal requiere NostoProductViewService."
                );
            }
        }

        init() {
            if (this.isInitialized) {
                return this;
            }

            this.validateDependencies();

            this.unbindLegacyQuickBuyTracking();

            this.content =
                this.modal.querySelector(
                    "[data-nosto-quick-buy-content]"
                );

            this.loadingElement =
                this.modal.querySelector(
                    "[data-nosto-quick-buy-loading]"
                );

            this.loadErrorElement =
                this.modal.querySelector(
                    "[data-nosto-quick-buy-load-error]"
                );

            if (!this.content) {
                throw new Error(
                    "El modal requiere [data-nosto-quick-buy-content]."
                );
            }

            document.addEventListener(
                "nosto:quick-buy:open",
                this.boundHandleOpenRequest
            );

            this.modal.addEventListener(
                "click",
                this.boundHandleClick
            );

            this.modal.addEventListener(
                "cancel",
                this.boundHandleCancel
            );

            this.modal.addEventListener(
                "close",
                this.boundHandleClose
            );

            document.addEventListener(
                "theme:product:add",
                this.boundHandleProductAdd
            );

            document.addEventListener(
                "theme:product:add-error",
                this.boundHandleProductAddError
            );

            this.isInitialized = true;

            return this;
        }

        async handleOpenRequest(event) {
            try {
                await this.open(
                    event.detail || {}
                );
            } catch (error) {
                if (
                    error?.name !==
                    "AbortError"
                ) {
                    console.error(
                        "[NostoQuickBuyModal] No fue posible abrir el modal.",
                        error
                    );
                }
            }
        }

        async open({
            productId = "",
            productHandle = "",
            productUrl = "",
            quickBuyUrl = "",
            button = null,
            hit = null,
            source = "",
            track = "",
            attributionKey = "",
            context = {},
        } = {}) {
            this.validateDependencies();

            this.triggerButton =
                button instanceof Element
                    ? button
                    : document.activeElement;

            const trackingContext = {
                ...context,

                source:
                    this.normalizeString(
                        source ||
                        context.source
                    ),

                track:
                    this.normalizeString(
                        track ||
                        context.track
                    ),

                attributionKey:
                    this.normalizeString(
                        attributionKey ||
                        context.attributionKey
                    ),
            };

            this.activeContext = {
                ...trackingContext,
            };

            /*
             * Cada apertura comienza con un nuevo estado
             * de colores.
             */
            this.colorOrder = [];
            this.activeColorIndex = 0;
            this.colorScrollLeft = 0;

            this.showDialog();

            this.onOpen?.({
                modal: this,

                productId:
                    this.normalizeId(productId),

                productHandle,
                productUrl,
                quickBuyUrl,
                hit,

                source:
                    trackingContext.source,

                track:
                    trackingContext.track,

                attributionKey:
                    trackingContext.attributionKey,

                context:
                    trackingContext,
            });

            await this.loadProduct({
                productId,

                handle:
                    productHandle,

                url:
                    quickBuyUrl ||
                    productUrl,

                hit,

                context:
                    trackingContext,

                initialLoad: true,
            });

            /*
             * Guardamos el orden inicial una vez que
             * el primer producto terminó de renderizar.
             */
            this.captureColorPosition();

            return this.root;
        }

        showDialog() {
            if (this.isOpen) {
                return;
            }

            this.modal.removeAttribute(
                "inert"
            );

            if (
                typeof this.modal.show ===
                "function" &&
                !this.modal.open
            ) {
                this.modal.show();
            } else {
                this.modal.setAttribute(
                    "open",
                    ""
                );
            }

            this.modal.classList.add(
                "is-open"
            );

            document.dispatchEvent(
                new CustomEvent(
                    "theme:quick-add:open",
                    {
                        bubbles: true,
                        detail: {
                            modal:
                                this.modal,
                        },
                    }
                )
            );

            document.dispatchEvent(
                new CustomEvent(
                    "theme:scroll:lock",
                    {
                        bubbles: true,
                        detail: {
                            modal:
                                this.modal,
                        },
                    }
                )
            );

            this.isOpen = true;
        }

        async loadProduct({
            productId = "",
            handle = "",
            url = "",
            hit = null,
            context = {},
            initialLoad = false,
        } = {}) {
            const requestId =
                ++this.requestSequence;

            this.cancelActiveRequest();

            const controller =
                new AbortController();

            this.activeRequestController =
                controller;

            this.setLoading(
                true,
                {
                    switching:
                        !initialLoad,
                }
            );

            this.hideLoadError();

            /*
             * Construimos la URL final aquí y no en
             * selectRelatedProduct().
             *
             * Esto es importante porque así el contexto
             * se conserva también cuando cambiamos de
             * color mediante AJAX.
             */
            const quickBuyRequestUrl =
                this.buildQuickBuyUrl(url);

            try {
                const html =
                    await this.viewService.getQuickBuy({
                        productId,
                        handle,
                        url:
                            quickBuyRequestUrl,
                        hit,
                        context,
                        signal:
                            controller.signal,
                    });

                if (
                    requestId !==
                    this.requestSequence ||
                    controller.signal.aborted
                ) {
                    return null;
                }

                this.insertContent(html);

                this.initializeContent();

                this.activeContext = {
                    ...this.activeContext,
                    ...context,
                };

                const canonicalProductUrl =
                    this.normalizeProductUrl(
                        hit?.url ||
                        this.root?.dataset
                            .productUrl ||
                        url
                    );

                this.activeProductUrl =
                    canonicalProductUrl;

                this.activeHit =
                    hit
                        ? {
                            ...hit,

                            productId:
                                this.normalizeId(
                                    hit.productId ||
                                    this.activeProductId
                                ),

                            url:
                                this.normalizeProductUrl(
                                    hit.url ||
                                    canonicalProductUrl
                                ),
                        }
                        : {
                            productId:
                                this.activeProductId,

                            url:
                                canonicalProductUrl,
                        };

                const detail = {
                    modal: this,
                    productId:
                        this.activeProductId,
                    productHandle:
                        this.activeHandle,
                    root:
                        this.root,
                    context,
                };

                this.onProductChange?.(
                    detail
                );

                this.modal.dispatchEvent(
                    new CustomEvent(
                        "nosto:quick-buy:product-change",
                        {
                            bubbles: true,
                            detail,
                        }
                    )
                );

                this.focusInitialElement();

                return this.root;
            } catch (error) {
                if (
                    error?.name ===
                    "AbortError"
                ) {
                    throw error;
                }

                this.showLoadError(
                    "No fue posible cargar este producto. Intenta nuevamente."
                );

                this.onError?.({
                    modal: this,
                    error,
                    productId,
                    handle,
                    context,
                });

                throw error;
            } finally {
                if (
                    requestId ===
                    this.requestSequence
                ) {
                    this.activeRequestController =
                        null;

                    this.setLoading(false);
                }
            }
        }

        cancelActiveRequest() {
            if (
                !this.activeRequestController
            ) {
                return;
            }

            this.activeRequestController.abort();

            this.activeRequestController =
                null;
        }

        insertContent(html) {
            const template =
                document.createElement(
                    "template"
                );

            template.innerHTML =
                this.normalizeString(html);

            const root =
                template.content.querySelector(
                    "[data-nosto-quick-buy]"
                );

            if (!root) {
                throw new Error(
                    "La vista no contiene [data-nosto-quick-buy]."
                );
            }

            this.content.replaceChildren(
                template.content
            );
        }

        initializeContent() {
            this.root =
                this.content.querySelector(
                    "[data-nosto-quick-buy]"
                );

            if (!this.root) {
                throw new Error(
                    "No fue posible inicializar el contenido del quick buy."
                );
            }

            this.activeProductId =
                this.normalizeId(
                    this.root.dataset
                        .productId
                );

            this.activeHandle =
                this.normalizeString(
                    this.root.dataset
                        .productHandle
                );

            this.pendingProductId = "";

            this.modal.dataset.productId =
                this.activeProductId;

            const colorSection =
                this.root.querySelector(
                    ".nosto-quick-buy__colors"
                );

            const productImages =
                this.root.querySelector(
                    "product-images"
                );

            const colorLabel =
                this.root.querySelector(
                    ".nosto-quick-buy__colors-header"
                );

            const isProductPage =
                this.isProductPage();

            /*
            * COLORES / SWATCHES
            */
            if (colorSection) {
                if (isProductPage) {
                    colorSection.hidden = true;
                    colorSection.style.setProperty(
                        "display",
                        "none",
                        "important"
                    );
                } else {
                    colorSection.hidden = false;
                    colorSection.style.removeProperty(
                        "display"
                    );
                }
            }

            /*
            * IMÁGENES
            */
            if (productImages) {
                if (isProductPage) {
                    productImages.hidden = true;
                    productImages.style.setProperty(
                        "display",
                        "none",
                        "important"
                    );
                } else {
                    productImages.hidden = false;
                    productImages.style.removeProperty(
                        "display"
                    );
                }
            }

            /*
            * COLOR: Nombre del color
            *
            * SOLO se muestra en página de producto.
            */
            if (colorLabel) {
                if (isProductPage) {
                    colorLabel.hidden = false;
                    colorLabel.style.removeProperty(
                        "display"
                    );
                } else {
                    colorLabel.hidden = true;
                    colorLabel.style.setProperty(
                        "display",
                        "none",
                        "important"
                    );
                }
            }

            this.normalizeActiveSwatch();

            this.initializeColorSlider();

            this.initializeImageSlider();
        }

        /* =========================================================
         * COLOR POSITION
         * ========================================================= */

        /**
         * Obtiene todos los IDs de colores en el orden actual.
         */
        getCurrentColorOrder() {
            if (!this.root) {
                return [];
            }

            return Array.from(
                this.root.querySelectorAll(
                    "[data-nosto-quick-buy-swatch]"
                )
            )
                .map((swatch) =>
                    this.normalizeId(
                        swatch.dataset.productId
                    )
                )
                .filter(Boolean);
        }

        /**
         * Guarda:
         * - orden de colores;
         * - índice del producto actual;
         * - scroll horizontal.
         */
        captureColorPosition() {
            const slider =
                this.root?.querySelector(
                    "[data-nosto-quick-buy-color-slider]"
                );

            const order =
                this.getCurrentColorOrder();

            if (order.length) {
                this.colorOrder = order;

                const index =
                    order.indexOf(
                        this.activeProductId
                    );

                this.activeColorIndex =
                    index >= 0
                        ? index
                        : 0;
            }

            if (slider) {
                this.colorScrollLeft =
                    slider.scrollLeft;
            }

            this.log(
                "Posición de colores guardada:",
                {
                    order: this.colorOrder,
                    activeProductId:
                        this.activeProductId,
                    activeColorIndex:
                        this.activeColorIndex,
                    scrollLeft:
                        this.colorScrollLeft,
                }
            );
        }

        /**
         * Reordena el nuevo HTML usando exactamente el
         * orden que tenía el slider antes del AJAX.
         *
         * El producto seleccionado NO pasa al principio.
         */
        restoreColorPosition() {
            const slider =
                this.root?.querySelector(
                    "[data-nosto-quick-buy-color-slider]"
                );

            if (!slider) {
                return;
            }

            const swatches =
                Array.from(
                    slider.querySelectorAll(
                        "[data-nosto-quick-buy-swatch]"
                    )
                );

            if (!swatches.length) {
                return;
            }

            /*
             * Si todavía no tenemos un orden anterior,
             * no hacemos nada.
             */
            if (!this.colorOrder.length) {
                return;
            }

            const swatchMap =
                new Map();

            swatches.forEach(
                (swatch) => {
                    const id =
                        this.normalizeId(
                            swatch.dataset.productId
                        );

                    if (id) {
                        swatchMap.set(
                            id,
                            swatch
                        );
                    }
                }
            );

            /*
             * Primero respetamos exactamente el orden
             * anterior.
             */
            const orderedSwatches = [];

            this.colorOrder.forEach(
                (productId) => {
                    const swatch =
                        swatchMap.get(
                            productId
                        );

                    if (swatch) {
                        orderedSwatches.push(
                            swatch
                        );

                        swatchMap.delete(
                            productId
                        );
                    }
                }
            );

            /*
             * Si apareció algún color nuevo que no existía
             * anteriormente, lo dejamos al final.
             */
            swatchMap.forEach(
                (swatch) => {
                    orderedSwatches.push(
                        swatch
                    );
                }
            );

            /*
             * Reinsertamos los mismos nodos.
             * No clonamos ni recreamos botones.
             */
            const fragment =
                document.createDocumentFragment();

            orderedSwatches.forEach(
                (swatch) => {
                    fragment.appendChild(
                        swatch
                    );
                }
            );

            slider.replaceChildren(
                fragment
            );

            /*
             * Actualizamos el estado activo.
             */
            this.normalizeActiveSwatch();

            /*
             * El nuevo producto debe conservar
             * exactamente el índice que tenía.
             */
            const newIndex =
                orderedSwatches.findIndex(
                    (swatch) =>
                        this.normalizeId(
                            swatch.dataset
                                .productId
                        ) ===
                        this.activeProductId
                );

            if (newIndex >= 0) {
                this.activeColorIndex =
                    newIndex;
            }

            this.log(
                "Posición de colores restaurada:",
                {
                    order:
                        orderedSwatches.map(
                            (swatch) =>
                                this.normalizeId(
                                    swatch.dataset
                                        .productId
                                )
                        ),
                    activeProductId:
                        this.activeProductId,
                    activeColorIndex:
                        this.activeColorIndex,
                }
            );

            /*
             * Restauramos scroll después de que el DOM
             * y el layout hayan sido recalculados.
             */
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const newSlider =
                        this.root?.querySelector(
                            "[data-nosto-quick-buy-color-slider]"
                        );

                    if (!newSlider) {
                        return;
                    }

                    const maxScroll =
                        Math.max(
                            0,
                            newSlider.scrollWidth -
                            newSlider.clientWidth
                        );

                    const targetScroll =
                        Math.min(
                            this.colorScrollLeft,
                            maxScroll
                        );

                    console.log(
                        "[NostoColorSlider] Restaurando scroll",
                        {
                            saved:
                                this.colorScrollLeft,
                            maxScroll,
                            target:
                                targetScroll,
                        }
                    );

                    newSlider.scrollLeft =
                        targetScroll;
                });
            });
        }

        initializeImageSlider() {
            if (
                typeof this.destroyImageSlider ===
                "function"
            ) {
                this.destroyImageSlider();
                this.destroyImageSlider = null;
            }

            const gallery =
                this.root?.querySelector(
                    "[data-nosto-quick-buy-image-slider]"
                );

            if (!gallery) {
                return;
            }

            const track =
                gallery.querySelector(
                    "[data-product-media-list]"
                );

            if (!track) {
                return;
            }

            const slides =
                Array.from(
                    track.querySelectorAll(
                        ".product__slide"
                    )
                );

            if (!slides.length) {
                return;
            }

            const prevButton =
                gallery.querySelector(
                    "[data-nosto-quick-buy-image-prev]"
                );

            const nextButton =
                gallery.querySelector(
                    "[data-nosto-quick-buy-image-next]"
                );

            let pagination =
                gallery.querySelector(
                    "[data-nosto-quick-buy-image-pagination]"
                );

            if (!pagination) {
                pagination =
                    document.createElement(
                        "div"
                    );

                pagination.className =
                    "nosto-quick-buy__image-pagination";

                pagination.setAttribute(
                    "data-nosto-quick-buy-image-pagination",
                    ""
                );

                pagination.setAttribute(
                    "aria-label",
                    "Imágenes del producto"
                );

                gallery.appendChild(
                    pagination
                );
            }

            pagination.replaceChildren();

            const dots =
                slides.map(
                    (_, index) => {
                        const dot =
                            document.createElement(
                                "button"
                            );

                        dot.type = "button";

                        dot.className =
                            "nosto-quick-buy__image-dot";

                        dot.setAttribute(
                            "data-nosto-quick-buy-image-dot",
                            ""
                        );

                        dot.dataset.slideIndex =
                            String(index);

                        dot.setAttribute(
                            "aria-label",
                            `Ir a imagen ${index + 1}`
                        );

                        dot.setAttribute(
                            "aria-current",
                            index === 0
                                ? "true"
                                : "false"
                        );

                        dot.classList.toggle(
                            "is-active",
                            index === 0
                        );

                        pagination.appendChild(
                            dot
                        );

                        return dot;
                    }
                );

            let currentIndex =
                Math.max(
                    0,
                    Math.min(
                        this.activeImageIndex || 0,
                        slides.length - 1
                    )
                );

            let pointerId = null;

            let startX = 0;
            let startY = 0;

            let currentX = 0;
            let currentY = 0;

            let isDragging = false;
            let dragAxis = null;

            const getGap = () => {
                const styles =
                    global.getComputedStyle(
                        track
                    );

                return (
                    parseFloat(
                        styles.columnGap ||
                        styles.gap ||
                        "0"
                    ) || 0
                );
            };

            const getSlideWidth = () => {
                return (
                    slides[0]
                        ?.getBoundingClientRect()
                        .width || 0
                );
            };

            const getStep = () => {
                return (
                    getSlideWidth() +
                    getGap()
                );
            };

            const getTrackWidth = () => {
                const slideWidth =
                    getSlideWidth();

                const gap =
                    getGap();

                return (
                    slides.length *
                    slideWidth +
                    Math.max(
                        0,
                        slides.length - 1
                    ) *
                    gap
                );
            };

            const getMaxTranslate = () => {
                return Math.max(
                    0,
                    getTrackWidth() -
                    gallery.clientWidth
                );
            };

            const getMaxIndex = () => {
                const step =
                    getStep();

                if (!step) {
                    return 0;
                }

                return Math.min(
                    slides.length - 1,
                    Math.ceil(
                        getMaxTranslate() /
                        step
                    )
                );
            };

            const getOffsetForIndex = (
                index
            ) => {
                return Math.min(
                    index * getStep(),
                    getMaxTranslate()
                );
            };

            const updateControls = () => {
                const maxIndex =
                    getMaxIndex();

                const hasOverflow =
                    maxIndex > 0;

                if (prevButton) {
                    prevButton.hidden =
                        !hasOverflow;

                    prevButton.disabled =
                        !hasOverflow ||
                        currentIndex <= 0;
                }

                if (nextButton) {
                    nextButton.hidden =
                        !hasOverflow;

                    nextButton.disabled =
                        !hasOverflow ||
                        currentIndex >= maxIndex;
                }

                dots.forEach(
                    (dot, index) => {
                        const isReachable =
                            index <= maxIndex;

                        dot.hidden =
                            !isReachable;

                        const isActive =
                            isReachable &&
                            index ===
                            currentIndex;

                        dot.classList.toggle(
                            "is-active",
                            isActive
                        );

                        dot.setAttribute(
                            "aria-current",
                            isActive
                                ? "true"
                                : "false"
                        );
                    }
                );

                pagination.hidden =
                    maxIndex <= 0;
            };

            const setTransform = (
                offset,
                animate = true
            ) => {
                track.style.transition =
                    animate
                        ? "transform 0.3s ease"
                        : "none";

                track.style.transform =
                    `translate3d(${-offset}px, 0, 0)`;
            };

            const goTo = (
                nextIndex,
                {
                    animate = true,
                } = {}
            ) => {
                const maxIndex =
                    getMaxIndex();

                currentIndex =
                    Math.max(
                        0,
                        Math.min(
                            nextIndex,
                            maxIndex
                        )
                    );

                this.activeImageIndex =
                    currentIndex;

                setTransform(
                    getOffsetForIndex(
                        currentIndex
                    ),
                    animate
                );

                updateControls();
            };

            const handlePrev =
                (event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    goTo(
                        currentIndex - 1
                    );
                };

            const handleNext =
                (event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    goTo(
                        currentIndex + 1
                    );
                };

            prevButton?.addEventListener(
                "click",
                handlePrev
            );

            nextButton?.addEventListener(
                "click",
                handleNext
            );

            const dotHandlers =
                new Map();

            dots.forEach(
                (dot, index) => {
                    const handler =
                        () => {
                            goTo(index);
                        };

                    dotHandlers.set(
                        dot,
                        handler
                    );

                    dot.addEventListener(
                        "click",
                        handler
                    );
                }
            );

            const handlePointerDown =
                (event) => {
                    if (
                        event.pointerType ===
                        "mouse" &&
                        event.button !== 0
                    ) {
                        return;
                    }

                    pointerId =
                        event.pointerId;

                    startX =
                        event.clientX;

                    startY =
                        event.clientY;

                    currentX =
                        startX;

                    currentY =
                        startY;

                    dragAxis = null;
                    isDragging = true;

                    track.style.transition =
                        "none";
                };

            const handlePointerMove =
                (event) => {
                    if (
                        !isDragging ||
                        event.pointerId !==
                        pointerId
                    ) {
                        return;
                    }

                    currentX =
                        event.clientX;

                    currentY =
                        event.clientY;

                    const deltaX =
                        currentX - startX;

                    const deltaY =
                        currentY - startY;

                    if (!dragAxis) {
                        if (
                            Math.abs(deltaX) <
                            8 &&
                            Math.abs(deltaY) <
                            8
                        ) {
                            return;
                        }

                        dragAxis =
                            Math.abs(deltaX) >
                                Math.abs(deltaY)
                                ? "x"
                                : "y";
                    }

                    if (
                        dragAxis !== "x"
                    ) {
                        return;
                    }

                    const baseOffset =
                        getOffsetForIndex(
                            currentIndex
                        );

                    let nextOffset =
                        baseOffset -
                        deltaX;

                    if (nextOffset < 0) {
                        nextOffset *= 0.25;
                    }

                    const maxTranslate =
                        getMaxTranslate();

                    if (
                        nextOffset >
                        maxTranslate
                    ) {
                        nextOffset =
                            maxTranslate +
                            (
                                nextOffset -
                                maxTranslate
                            ) *
                            0.25;
                    }

                    setTransform(
                        nextOffset,
                        false
                    );
                };

            const finishPointer =
                (event) => {
                    if (
                        !isDragging ||
                        (
                            pointerId !== null &&
                            event.pointerId !==
                            pointerId
                        )
                    ) {
                        return;
                    }

                    const deltaX =
                        currentX -
                        startX;

                    const deltaY =
                        currentY -
                        startY;

                    const wasHorizontal =
                        dragAxis === "x";

                    isDragging = false;
                    dragAxis = null;
                    pointerId = null;

                    if (!wasHorizontal) {
                        goTo(
                            currentIndex
                        );

                        return;
                    }

                    const threshold =
                        Math.min(
                            70,
                            Math.max(
                                35,
                                getSlideWidth() *
                                0.16
                            )
                        );

                    if (
                        Math.abs(deltaX) <
                        threshold ||
                        Math.abs(deltaX) <
                        Math.abs(deltaY)
                    ) {
                        goTo(
                            currentIndex
                        );

                        return;
                    }

                    if (deltaX < 0) {
                        goTo(
                            currentIndex + 1
                        );
                    } else {
                        goTo(
                            currentIndex - 1
                        );
                    }
                };

            track.addEventListener(
                "pointerdown",
                handlePointerDown
            );

            track.addEventListener(
                "pointermove",
                handlePointerMove
            );

            track.addEventListener(
                "pointerup",
                finishPointer
            );

            track.addEventListener(
                "pointercancel",
                finishPointer
            );

            const handleDragStart =
                (event) => {
                    event.preventDefault();
                };

            track.addEventListener(
                "dragstart",
                handleDragStart
            );

            let resizeObserver = null;

            if (
                typeof ResizeObserver !==
                "undefined"
            ) {
                resizeObserver =
                    new ResizeObserver(
                        () => {
                            goTo(
                                currentIndex,
                                {
                                    animate: false,
                                }
                            );
                        }
                    );

                resizeObserver.observe(
                    gallery
                );
            }

            this.destroyImageSlider =
                () => {
                    prevButton?.removeEventListener(
                        "click",
                        handlePrev
                    );

                    nextButton?.removeEventListener(
                        "click",
                        handleNext
                    );

                    dots.forEach(
                        (dot) => {
                            const handler =
                                dotHandlers.get(
                                    dot
                                );

                            if (handler) {
                                dot.removeEventListener(
                                    "click",
                                    handler
                                );
                            }
                        }
                    );

                    track.removeEventListener(
                        "pointerdown",
                        handlePointerDown
                    );

                    track.removeEventListener(
                        "pointermove",
                        handlePointerMove
                    );

                    track.removeEventListener(
                        "pointerup",
                        finishPointer
                    );

                    track.removeEventListener(
                        "pointercancel",
                        finishPointer
                    );

                    track.removeEventListener(
                        "dragstart",
                        handleDragStart
                    );

                    resizeObserver?.disconnect();

                    track.style.removeProperty(
                        "transition"
                    );

                    track.style.removeProperty(
                        "transform"
                    );
                };

            requestAnimationFrame(() => {
                goTo(
                    currentIndex,
                    {
                        animate: false,
                    }
                );
            });
        }

        /* =========================================================
         * COLOR SLIDER
         * ========================================================= */
        initializeColorSlider() {
            if (
                typeof this.destroyColorSlider === "function"
            ) {
                this.destroyColorSlider();
                this.destroyColorSlider = null;
            }

            const slider =
                this.root?.querySelector(
                    "[data-nosto-quick-buy-color-slider]"
                );

            if (!slider) {
                return;
            }

            const wrapper =
                slider.closest(
                    ".nosto-quick-buy__color-slider-wrapper"
                );

            const prevButton =
                this.root.querySelector(
                    "[data-nosto-quick-buy-color-prev]"
                );

            const nextButton =
                this.root.querySelector(
                    "[data-nosto-quick-buy-color-next]"
                );

            if (
                !wrapper ||
                !prevButton ||
                !nextButton
            ) {
                return;
            }

            /* =========================================================
            * CONFIGURACIÓN
            * ========================================================= */

            slider.style.overflowX = "auto";
            slider.style.overflowY = "hidden";

            /*
            * Vertical:
            * dejamos que la página haga scroll.
            *
            * Horizontal:
            * lo controlamos nosotros.
            */
            slider.style.touchAction = "pan-y";

            slider.style.webkitOverflowScrolling =
                "touch";

            slider.style.scrollSnapType = "none";

            slider.style.scrollBehavior = "auto";

            slider.style.overscrollBehaviorX =
                "contain";

            slider.style.userSelect =
                "none";

            slider.style.webkitUserSelect =
                "none";

            /* =========================================================
            * HELPERS
            * ========================================================= */

            const items = () =>
                Array.from(
                    slider.querySelectorAll(
                        ".nosto-quick-buy__color-item"
                    )
                );

            const getMaxScroll = () => {
                return Math.max(
                    0,
                    slider.scrollWidth -
                    slider.clientWidth
                );
            };

            /* =========================================================
            * ESTADO DE FLECHAS
            * ========================================================= */

            const updateState = () => {
                const maxScroll =
                    getMaxScroll();

                const currentScroll =
                    Math.max(
                        0,
                        Math.min(
                            slider.scrollLeft,
                            maxScroll
                        )
                    );

                const overflow =
                    maxScroll > 2;

                wrapper.classList.toggle(
                    "has-overflow",
                    overflow
                );

                prevButton.hidden =
                    !overflow;

                nextButton.hidden =
                    !overflow;

                prevButton.disabled =
                    !overflow ||
                    currentScroll <= 2;

                nextButton.disabled =
                    !overflow ||
                    currentScroll >=
                    maxScroll - 2;
            };

            /* =========================================================
            * DISTANCIA DE LAS FLECHAS
            * ========================================================= */

            const getScrollDistance = () => {
                const sliderItems =
                    items();

                if (!sliderItems.length) {
                    return Math.max(
                        80,
                        slider.clientWidth * 0.75
                    );
                }

                const firstItem =
                    sliderItems[0];

                const styles =
                    global.getComputedStyle(
                        slider
                    );

                const gap =
                    parseFloat(
                        styles.columnGap ||
                        styles.gap ||
                        "0"
                    ) || 0;

                const itemWidth =
                    firstItem
                        .getBoundingClientRect()
                        .width;

                const visibleItems =
                    Math.max(
                        1,
                        Math.floor(
                            slider.clientWidth /
                            (itemWidth + gap)
                        )
                    );

                return (
                    (itemWidth + gap) *
                    Math.max(
                        1,
                        Math.min(
                            visibleItems - 1,
                            3
                        )
                    )
                );
            };

            /* =========================================================
            * FLECHAS
            * ========================================================= */

            const scrollByDirection = (
                direction
            ) => {
                const maxScroll =
                    getMaxScroll();

                const distance =
                    getScrollDistance();

                const current =
                    slider.scrollLeft;

                const target =
                    direction < 0
                        ? Math.max(
                            0,
                            current - distance
                        )
                        : Math.min(
                            maxScroll,
                            current + distance
                        );

                slider.scrollTo({
                    left: target,
                    behavior: "smooth",
                });
            };

            const handlePrev = (
                event
            ) => {
                event.preventDefault();
                event.stopPropagation();

                scrollByDirection(-1);
            };

            const handleNext = (
                event
            ) => {
                event.preventDefault();
                event.stopPropagation();

                scrollByDirection(1);
            };

            prevButton.addEventListener(
                "click",
                handlePrev
            );

            nextButton.addEventListener(
                "click",
                handleNext
            );

            /* =========================================================
            * TOUCH SWIPE
            * ========================================================= */

            let touchStartX = 0;
            let touchStartY = 0;

            let startScrollLeft = 0;

            let isTouching = false;
            let isHorizontalSwipe = false;

            let touchAnimationFrame = null;

            let latestTouchX = 0;

            let suppressClick = false;

            /* =========================================================
            * ACTUALIZAR SCROLL
            * ========================================================= */

            const updateTouchScroll = () => {
                touchAnimationFrame = null;

                if (
                    !isTouching ||
                    !isHorizontalSwipe
                ) {
                    return;
                }

                const deltaX =
                    latestTouchX -
                    touchStartX;

                const maxScroll =
                    getMaxScroll();

                const newScrollLeft =
                    Math.max(
                        0,
                        Math.min(
                            startScrollLeft -
                            deltaX,
                            maxScroll
                        )
                    );

                slider.scrollLeft =
                    newScrollLeft;
            };

            const requestTouchScroll = () => {
                if (
                    touchAnimationFrame !== null
                ) {
                    return;
                }

                touchAnimationFrame =
                    requestAnimationFrame(
                        updateTouchScroll
                    );
            };

            /* =========================================================
            * TOUCH START
            * ========================================================= */

            const handleTouchStart = (
                event
            ) => {
                if (
                    !event.touches ||
                    !event.touches.length
                ) {
                    return;
                }

                const touch =
                    event.touches[0];

                touchStartX =
                    touch.clientX;

                touchStartY =
                    touch.clientY;

                latestTouchX =
                    touch.clientX;

                startScrollLeft =
                    slider.scrollLeft;

                isTouching = true;
                isHorizontalSwipe = false;

                suppressClick = false;
            };

            /* =========================================================
            * TOUCH MOVE
            * ========================================================= */

            const handleTouchMove = (
                event
            ) => {
                if (
                    !isTouching ||
                    !event.touches ||
                    !event.touches.length
                ) {
                    return;
                }

                const touch =
                    event.touches[0];

                latestTouchX =
                    touch.clientX;

                const deltaX =
                    latestTouchX -
                    touchStartX;

                const deltaY =
                    touch.clientY -
                    touchStartY;

                /*
                * Esperamos a que el gesto tenga
                * suficiente movimiento para determinar
                * la dirección.
                */
                if (!isHorizontalSwipe) {
                    if (
                        Math.abs(deltaX) < 6 &&
                        Math.abs(deltaY) < 6
                    ) {
                        return;
                    }

                    /*
                    * Si es vertical:
                    * dejamos continuar el scroll
                    * normal de la página.
                    */
                    if (
                        Math.abs(deltaY) >
                        Math.abs(deltaX)
                    ) {
                        isTouching = false;

                        return;
                    }

                    /*
                    * A partir de aquí es horizontal.
                    */
                    isHorizontalSwipe = true;
                }

                if (!isHorizontalSwipe) {
                    return;
                }

                /*
                * MUY IMPORTANTE:
                * solamente bloqueamos el comportamiento
                * del navegador cuando ya sabemos que
                * el gesto es horizontal.
                */
                event.preventDefault();

                suppressClick =
                    Math.abs(deltaX) > 8;

                requestTouchScroll();
            };

            /* =========================================================
            * TOUCH END
            * ========================================================= */

            const handleTouchEnd = (
                event
            ) => {
                if (!isTouching) {
                    return;
                }

                /*
                * Aplicamos el último movimiento.
                */
                if (isHorizontalSwipe) {
                    if (
                        event.changedTouches &&
                        event.changedTouches.length
                    ) {
                        latestTouchX =
                            event.changedTouches[0]
                                .clientX;
                    }

                    if (
                        touchAnimationFrame !== null
                    ) {
                        cancelAnimationFrame(
                            touchAnimationFrame
                        );

                        touchAnimationFrame =
                            null;
                    }

                    updateTouchScroll();
                }

                isTouching = false;
                isHorizontalSwipe = false;

                if (suppressClick) {
                    setTimeout(() => {
                        suppressClick = false;
                    }, 100);
                }
            };

            /* =========================================================
            * TOUCH CANCEL
            * ========================================================= */

            const handleTouchCancel = () => {
                if (
                    touchAnimationFrame !== null
                ) {
                    cancelAnimationFrame(
                        touchAnimationFrame
                    );

                    touchAnimationFrame =
                        null;
                }

                isTouching = false;
                isHorizontalSwipe = false;
                suppressClick = false;
            };

            /* =========================================================
            * EVITAR CLICK DESPUÉS DEL SWIPE
            * ========================================================= */

            const handleSliderClick = (
                event
            ) => {
                if (!suppressClick) {
                    return;
                }

                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();

                suppressClick = false;
            };

            /* =========================================================
            * TOUCH EVENTS
            * ========================================================= */

            slider.addEventListener(
                "touchstart",
                handleTouchStart,
                {
                    passive: true,
                }
            );

            slider.addEventListener(
                "touchmove",
                handleTouchMove,
                {
                    passive: false,
                }
            );

            slider.addEventListener(
                "touchend",
                handleTouchEnd,
                {
                    passive: true,
                }
            );

            slider.addEventListener(
                "touchcancel",
                handleTouchCancel,
                {
                    passive: true,
                }
            );

            slider.addEventListener(
                "click",
                handleSliderClick,
                true
            );

            /* =========================================================
            * SCROLL
            * ========================================================= */

            const handleScroll = () => {
                updateState();
            };

            slider.addEventListener(
                "scroll",
                handleScroll,
                {
                    passive: true,
                }
            );

            /* =========================================================
            * DRAGSTART
            * ========================================================= */

            const handleDragStart = (
                event
            ) => {
                event.preventDefault();
            };

            slider.addEventListener(
                "dragstart",
                handleDragStart
            );

            slider
                .querySelectorAll("img")
                .forEach((image) => {
                    image.draggable = false;
                });

            /* =========================================================
            * RESIZE
            * ========================================================= */

            let resizeObserver = null;

            if (
                typeof ResizeObserver !==
                "undefined"
            ) {
                resizeObserver =
                    new ResizeObserver(() => {
                        requestAnimationFrame(() => {
                            updateState();
                        });
                    });

                resizeObserver.observe(
                    wrapper
                );

                resizeObserver.observe(
                    slider
                );
            }

            /* =========================================================
            * IMÁGENES
            * ========================================================= */

            const imageHandlers = [];

            slider
                .querySelectorAll("img")
                .forEach((image) => {
                    if (image.complete) {
                        return;
                    }

                    const handleImageLoad = () => {
                        requestAnimationFrame(
                            updateState
                        );
                    };

                    image.addEventListener(
                        "load",
                        handleImageLoad,
                        {
                            once: true,
                        }
                    );

                    image.addEventListener(
                        "error",
                        handleImageLoad,
                        {
                            once: true,
                        }
                    );

                    imageHandlers.push({
                        image,
                        handleImageLoad,
                    });
                });

            /* =========================================================
            * INIT
            * ========================================================= */

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    updateState();
                });
            });

            /* =========================================================
            * CLEANUP
            * ========================================================= */

            this.destroyColorSlider =
                () => {
                    if (
                        touchAnimationFrame !==
                        null
                    ) {
                        cancelAnimationFrame(
                            touchAnimationFrame
                        );

                        touchAnimationFrame =
                            null;
                    }

                    prevButton.removeEventListener(
                        "click",
                        handlePrev
                    );

                    nextButton.removeEventListener(
                        "click",
                        handleNext
                    );

                    slider.removeEventListener(
                        "touchstart",
                        handleTouchStart
                    );

                    slider.removeEventListener(
                        "touchmove",
                        handleTouchMove
                    );

                    slider.removeEventListener(
                        "touchend",
                        handleTouchEnd
                    );

                    slider.removeEventListener(
                        "touchcancel",
                        handleTouchCancel
                    );

                    slider.removeEventListener(
                        "click",
                        handleSliderClick,
                        true
                    );

                    slider.removeEventListener(
                        "scroll",
                        handleScroll
                    );

                    slider.removeEventListener(
                        "dragstart",
                        handleDragStart
                    );

                    resizeObserver?.disconnect();

                    imageHandlers.forEach(
                        ({
                            image,
                            handleImageLoad,
                        }) => {
                            image.removeEventListener(
                                "load",
                                handleImageLoad
                            );

                            image.removeEventListener(
                                "error",
                                handleImageLoad
                            );
                        }
                    );

                    slider.style.removeProperty(
                        "overflow-x"
                    );

                    slider.style.removeProperty(
                        "overflow-y"
                    );

                    slider.style.removeProperty(
                        "touch-action"
                    );

                    slider.style.removeProperty(
                        "-webkit-overflow-scrolling"
                    );

                    slider.style.removeProperty(
                        "scroll-snap-type"
                    );

                    slider.style.removeProperty(
                        "scroll-behavior"
                    );

                    slider.style.removeProperty(
                        "overscroll-behavior-x"
                    );

                    slider.style.removeProperty(
                        "user-select"
                    );

                    slider.style.removeProperty(
                        "-webkit-user-select"
                    );
                };
        }
        /* =========================================================
         * CLICK / COLOR CHANGE
         * ========================================================= */

        handleClick(event) {
            if (
                event.target ===
                this.modal
            ) {
                event.preventDefault();

                this.close();

                return;
            }

            const closeButton =
                event.target.closest(
                    [
                        "[data-nosto-quick-buy-close]",
                        "[data-quick-add-modal-close]",
                    ].join(",")
                );

            if (closeButton) {
                event.preventDefault();

                this.close();

                return;
            }

            const swatch =
                event.target.closest(
                    "[data-nosto-quick-buy-swatch]"
                );

            if (
                !swatch ||
                !this.modal.contains(
                    swatch
                )
            ) {
                return;
            }

            event.preventDefault();

            this.selectRelatedProduct(
                swatch
            );
        }

        async selectRelatedProduct(
            swatch
        ) {
            if (!swatch) {
                return;
            }

            const productId =
                this.normalizeId(
                    swatch.dataset
                        .productId
                );

            const handle =
                this.normalizeString(
                    swatch.dataset
                        .productHandle
                );

            const productUrl =
                this.normalizeString(
                    swatch.dataset
                        .productUrl
                );

            const quickBuyUrl =
                this.normalizeString(
                    swatch.dataset
                        .quickBuyUrl
                );

            if (
                !productId ||
                !handle ||
                productId ===
                this.activeProductId ||
                productId ===
                this.pendingProductId
            ) {
                return;
            }

            /*
             * ==========================================
             * MUY IMPORTANTE
             * ==========================================
             *
             * Antes de hacer AJAX guardamos:
             *
             * 1. orden exacto de los colores;
             * 2. posición del producto actual;
             * 3. scroll horizontal actual.
             *
             * Esto evita que el nuevo producto pase
             * automáticamente a la primera posición.
             */
            this.captureColorPosition();

            const previousProductId =
                this.activeProductId;

            const previousColorIndex =
                this.activeColorIndex;

            const previousColorScroll =
                this.colorScrollLeft;

            this.pendingProductId =
                productId;

            this.setColorSwitchingState({
                isLoading: true,
                activeSwatch: swatch,
            });

            try {
                await this.loadProduct({
                    productId,
                    handle,
                    url:
                        quickBuyUrl ||
                        productUrl,

                    context: {
                        ...this.activeContext,

                        source:
                            "quick-buy-color",

                        previousProductId:
                            previousProductId,

                        previousColorIndex:
                            previousColorIndex,
                    },

                    hit: {
                        productId:
                            this.normalizeId(
                                swatch.dataset
                                    .productId
                            ),

                        url:
                            this.normalizeProductUrl(
                                swatch.dataset
                                    .productUrl
                            ),
                    },

                    initialLoad: false,
                });

                /*
                 * El nuevo HTML ya está cargado.
                 *
                 * Ahora restauramos el orden anterior.
                 */
                this.colorScrollLeft =
                    previousColorScroll;

                this.activeColorIndex =
                    previousColorIndex;

                this.restoreColorPosition();

            } catch (error) {
                if (
                    error?.name !==
                    "AbortError"
                ) {
                    console.error(
                        "[NostoQuickBuyModal] No fue posible cambiar el color.",
                        error
                    );
                }
            } finally {
                if (
                    this.pendingProductId ===
                    productId
                ) {
                    this.pendingProductId =
                        "";
                }

                this.setColorSwitchingState({
                    isLoading: false,
                });
            }
        }

        normalizeActiveSwatch() {
            this.root
                ?.querySelectorAll(
                    "[data-nosto-quick-buy-swatch]"
                )
                .forEach((swatch) => {
                    const productId =
                        this.normalizeId(
                            swatch.dataset
                                .productId
                        );

                    const isActive =
                        productId ===
                        this.activeProductId;

                    swatch.classList.toggle(
                        "is-active",
                        isActive
                    );

                    swatch.setAttribute(
                        "aria-pressed",
                        String(isActive)
                    );

                    swatch.disabled =
                        isActive;

                    swatch.classList.remove(
                        "is-loading"
                    );

                    swatch.removeAttribute(
                        "aria-busy"
                    );
                });
        }

        setColorSwitchingState({
            isLoading,
            activeSwatch = null,
        } = {}) {
            const loading =
                Boolean(isLoading);

            this.root
                ?.querySelectorAll(
                    "[data-nosto-quick-buy-swatch]"
                )
                .forEach((swatch) => {
                    const productId =
                        this.normalizeId(
                            swatch.dataset
                                .productId
                        );

                    const isActive =
                        productId ===
                        this.activeProductId;

                    const isRequested =
                        swatch ===
                        activeSwatch;

                    swatch.disabled =
                        loading ||
                        isActive;

                    swatch.classList.toggle(
                        "is-loading",
                        loading &&
                        isRequested
                    );

                    swatch.setAttribute(
                        "aria-busy",
                        String(
                            loading &&
                            isRequested
                        )
                    );
                });
        }

        isOwnCartEvent(event) {
            const button =
                event.detail?.button;

            return (
                button instanceof Element &&
                this.modal.contains(
                    button
                )
            );
        }

        async handleProductAdd(event) {
            if (
                !this.isOpen ||
                !this.isOwnCartEvent(event)
            ) {
                return;
            }

            const form =
                event.detail?.button
                    ?.form ||
                event.detail?.button
                    ?.closest?.("form") ||
                null;

            const variantId =
                this.normalizeId(
                    form
                        ?.querySelector(
                            '[name="id"]'
                        )
                        ?.value
                );

            const detail = {
                modal: this,

                productId:
                    this.activeProductId,

                productHandle:
                    this.activeHandle,

                productUrl:
                    this.activeHit?.url ||
                    this.activeProductUrl,

                variantId:
                    this.normalizeId(
                        event.detail?.response
                            ?.variant_id ||
                        variantId
                    ),

                hit:
                    this.activeHit,

                context: {
                    ...this.activeContext,
                },

                response:
                    event.detail?.response ||
                    null,
            };

            this.modal.dispatchEvent(
                new CustomEvent(
                    "nosto:quick-buy:add",
                    {
                        bubbles: true,
                        detail,
                    }
                )
            );

            const trackingPromise =
                this.trackSuccessfulAdd(
                    detail
                );

            this.close();

            await trackingPromise;

            /*
             * Después del tracking: reenviar el carrito registra el
             * "Cart Product" al momento (aunque el usuario vaya directo
             * al checkout) y, como es una petición ev1, envía la cola de
             * clics de Nosto. Si el add to cart de búsqueda/PLP quedara
             * en cola hasta el próximo PDP, desplazaría la atribución
             * del clic real hacia este producto.
             */
            await this.resolveNostoClient()
                ?.resendCart?.();
        }

        handleProductAddError(event) {
            if (
                !this.isOpen ||
                !this.isOwnCartEvent(event)
            ) {
                return;
            }

            this.log(
                "El quick buy recibió un error del carrito.",
                event.detail
            );
            this.close();
        }

        setLoading(
            isLoading,
            {
                switching = false,
            } = {}
        ) {
            this.isLoading =
                Boolean(isLoading);

            this.modal.classList.toggle(
                "is-loading",
                this.isLoading &&
                !switching
            );

            this.modal.classList.toggle(
                "is-switching-color",
                this.isLoading &&
                switching
            );

            this.modal.setAttribute(
                "aria-busy",
                String(
                    this.isLoading
                )
            );

            if (this.loadingElement) {
                this.loadingElement.hidden =
                    !this.isLoading;
            }
        }

        showLoadError(message) {
            if (!this.loadErrorElement) {
                return;
            }

            this.loadErrorElement.textContent =
                message;

            this.loadErrorElement.hidden =
                false;
        }

        hideLoadError() {
            if (!this.loadErrorElement) {
                return;
            }

            this.loadErrorElement.hidden =
                true;

            this.loadErrorElement.textContent =
                "";
        }

        focusInitialElement() {
            requestAnimationFrame(() => {
                const focusTarget =
                    this.root?.querySelector(
                        [
                            "[data-nosto-quick-buy-swatch].is-active",
                            "input:checked + label",
                            "[data-add-to-cart]",
                        ].join(",")
                    );

                focusTarget?.focus?.();
            });
        }

        handleCancel(event) {
            event.preventDefault();

            this.close();
        }

        handleClose() {
            if (!this.isOpen) {
                return;
            }

            this.finishClose();
        }

        close() {
            if (!this.isOpen) {
                return;
            }

            this.cancelActiveRequest();

            if (
                typeof this.modal.close ===
                "function" &&
                this.modal.open
            ) {
                this.modal.close();

                return;
            }

            this.modal.removeAttribute(
                "open"
            );

            this.finishClose();
        }

        finishClose() {
            this.cancelActiveRequest();

            this.pauseAllMedia();

            this.destroyImageSlider?.();
            this.destroyColorSlider?.();

            this.destroyImageSlider = null;
            this.destroyColorSlider = null;

            this.modal.classList.remove(
                "is-open",
                "is-loading",
                "is-switching-color"
            );

            this.modal.setAttribute(
                "inert",
                ""
            );

            this.modal.removeAttribute(
                "aria-busy"
            );

            this.hideLoadError();

            document.dispatchEvent(
                new CustomEvent(
                    "theme:tooltip:close",
                    {
                        bubbles: true,
                        detail: {
                            hideTransition:
                                true,
                            immediate:
                                true,
                        },
                    }
                )
            );

            if (
                !global.theme
                    ?.hasOpenModals?.()
            ) {
                document.dispatchEvent(
                    new CustomEvent(
                        "theme:scroll:unlock",
                        {
                            bubbles: true,
                        }
                    )
                );
            }

            const detail = {
                modal: this,

                productId:
                    this.activeProductId,

                productHandle:
                    this.activeHandle,
            };

            this.onClose?.(
                detail
            );

            this.isOpen = false;
            this.isLoading = false;
            this.pendingProductId = "";

            requestAnimationFrame(() => {
                this.triggerButton
                    ?.focus?.();

                this.triggerButton =
                    null;
            });
        }

        pauseAllMedia() {
            this.root
                ?.querySelectorAll(
                    "video"
                )
                .forEach((video) => {
                    video.pause();
                });
        }

        destroy() {
            if (!this.isInitialized) {
                return;
            }

            this.cancelActiveRequest();

            this.destroyImageSlider?.();
            this.destroyColorSlider?.();

            document.removeEventListener(
                "nosto:quick-buy:open",
                this.boundHandleOpenRequest
            );

            this.modal.removeEventListener(
                "click",
                this.boundHandleClick
            );

            this.modal.removeEventListener(
                "cancel",
                this.boundHandleCancel
            );

            this.modal.removeEventListener(
                "close",
                this.boundHandleClose
            );

            document.removeEventListener(
                "theme:product:add",
                this.boundHandleProductAdd
            );

            document.removeEventListener(
                "theme:product:add-error",
                this.boundHandleProductAddError
            );

            this.isInitialized = false;
        }
    }

    global.NostoQuickBuyModal =
        NostoQuickBuyModal;

})(window);